import { Input } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { cmToInch, FOOT_IN_INCHES, kgToPounds } from "src/lib/unitConversion";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import { parseNumberInput } from "src/ui/components/HeightInput/HeightInput";
import { FormErrorContext } from "../QuestionnaireStep/FormErrorContext";
import {
  findPairedWeightFieldId,
  validateBodyMetrics
} from "./BodyMetricsInput.helpers";

const Fieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;

  & + & {
    margin-top: 1.5em;
  }
`;

const Legend = styled.legend`
  margin: 0 0 0.5em;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--Greys-Charcoal80, rgba(33, 33, 33, 0.8));
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 1em;
`;

const ErrorMessage = styled.div`
  padding: 0.5em 1em 1em;
  display: block;
  color: var(--primary-warning-red, #bf1902);
  font-size: 0.875rem;

  @keyframes show {
    from {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: show 0.2s linear forwards;
`;

/**
 * Renders height (feet/inches) and weight (pounds) together on one screen,
 * but saves them as two real answers so each stays a plain number: height
 * under this field's own id, weight under the id of the hidden
 * `[only-logic]` question this field is paired with via
 * `[weight-field-ref="<ref>"]` (see BodyMetricsInput.helpers). Routed to by
 * `field.properties.body_metrics_field` in DynamicInput.
 */
const BodyMetricsInput: FC<{
  field: QuestionnaireField;
}> = (props) => {
  const heightFieldId = props.field.id;
  const { getValues, setValue } = useFormContext();
  const [{ fields }, { saveValue, parseAnswerOnlyValue }] =
    useQuestionnaireCubitInContext();
  const { setFieldError, clearFieldError } = React.useContext(FormErrorContext);
  const [heightError, setHeightError] = useState<string | undefined>(undefined);
  const [weightError, setWeightError] = useState<string | undefined>(undefined);

  const weightFieldId = findPairedWeightFieldId(
    fields,
    props.field.properties?.weight_field_ref
  );

  // `fields` (and so `weightFieldId`) can still be empty on the very first
  // render (e.g. the questionnaire structure hasn't finished loading yet),
  // one render behind height's value, which always arrives synchronously via
  // react-hook-form's defaultValues. ft/inches/pounds below are plain refs
  // seeded once on mount — remounting via `key` once weight's data is
  // actually available (rather than trying to patch the refs and the
  // uncontrolled inputs' already-rendered defaultValue in place) is what
  // makes the prefill land: a fresh mount reseeds every ref, and the
  // Input's defaultValue, from the now-correct values.
  const hasWeightFieldRef = Boolean(props.field.properties?.weight_field_ref);
  const isPrefillReady = !hasWeightFieldRef || weightFieldId !== undefined;

  useEffect(() => {
    if (!weightFieldId) {
      reportErrorSentry(
        new Error(
          "BodyMetricsInput: no paired weight field found for weight_field_ref"
        ),
        { extra: { fieldId: heightFieldId } }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report once per mount, not on every fields/weightFieldId identity change
  }, []);

  const heightCm = Number(getValues(heightFieldId) ?? 0);
  // Read weight's prefill from customFormData (via parseAnswerOnlyValue),
  // not react-hook-form's getValues: react-hook-form's per-step defaultValues
  // only ever seed the *current step's own* field ids, and the hidden weight
  // field is never part of that list, so getValues(weightFieldId) is always
  // blank on mount regardless of whether a real answer was already saved
  // (e.g. resuming the questionnaire after a page refresh).
  const weightKg = weightFieldId
    ? Number(parseAnswerOnlyValue(weightFieldId).value ?? 0)
    : 0;

  const inInches = useMemo(() => cmToInch(heightCm), [heightCm]);
  const inPounds = useMemo(() => kgToPounds(weightKg), [weightKg]);

  const ft = useRef(Math.floor(inInches / FOOT_IN_INCHES));
  const inches = useRef(Math.round(inInches % FOOT_IN_INCHES));
  const pounds = useRef(Math.round(inPounds));

  const commitValue = (): void => {
    clearFieldError(heightFieldId);

    const result = validateBodyMetrics({
      feet: ft.current,
      inches: inches.current,
      pounds: pounds.current,
      hasWeightField: Boolean(weightFieldId)
    });

    const heightErrorMsg =
      result.heightStatus === "out-of-range"
        ? translate("error_validate_height_invalid")
        : undefined;
    const weightErrorMsg =
      result.weightStatus === "out-of-range"
        ? translate("error_validate_weight_invalid")
        : undefined;
    // Height's blankness is caught for free by QuestionnaireStep's generic
    // required check (it reads customFormData[heightFieldId] directly), but
    // weight lives under a different field id that check never looks at, so
    // a missing weight has to block Continue explicitly here.
    const isWeightMissing = result.weightStatus === "missing";

    setHeightError(heightErrorMsg);
    setWeightError(weightErrorMsg);

    if (heightErrorMsg ?? weightErrorMsg ?? isWeightMissing) {
      setFieldError(
        heightFieldId,
        heightErrorMsg ??
          weightErrorMsg ??
          translate("required_field", {
            label: translate("bodyMetrics.weight.label")
          })
      );
    }

    saveValue(heightFieldId, result.heightCm);
    setValue(heightFieldId, result.heightCm);

    if (weightFieldId) {
      saveValue(weightFieldId, result.weightKg);
      setValue(weightFieldId, result.weightKg);
    }
  };

  const handleFeetChange = (newFtString: string): void => {
    ft.current = parseNumberInput(newFtString);
    commitValue();
  };

  const handleInchesChange = (newInchesString: string): void => {
    inches.current = parseNumberInput(newInchesString);
    commitValue();
  };

  const handlePoundsChange = (newPoundsString: string): void => {
    pounds.current = parseNumberInput(newPoundsString);
    commitValue();
  };

  useEffect(() => {
    commitValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when the prefilled cm/kg answer changes, not on every render
  }, [inInches, inPounds]);

  const initialValues = {
    ft: ft.current ? `${ft.current}` : "",
    inch: inches.current ? `${inches.current}` : "",
    lbs: pounds.current ? `${pounds.current}` : ""
  };

  return (
    <div key={isPrefillReady ? "ready" : "pending"}>
      <Fieldset>
        <Legend>{translate("bodyMetrics.height.label")}</Legend>
        <Grid>
          <Input
            name="feet"
            defaultValue={initialValues.ft}
            placeholder={translate("feet")}
            label={translate("feet")}
            isRequired
            maxLength={1}
            pattern="[0-9]*"
            mask="0"
            inputMode="numeric"
            aria-label={translate("feet")}
            icon={translate("units.feet")}
            onChange={(e) => handleFeetChange(e)}
          />
          <Input
            name="inches"
            defaultValue={initialValues.inch}
            placeholder={translate("inches")}
            label={translate("inches")}
            maxLength={2}
            mask="0[0]"
            pattern="[0-9]*"
            inputMode="numeric"
            aria-label={translate("inches")}
            icon={translate("units.inches")}
            onChange={(e) => handleInchesChange(e)}
          />
        </Grid>
        {heightError && (
          <ErrorMessage className="little1">{heightError}</ErrorMessage>
        )}
      </Fieldset>
      <Fieldset>
        <Legend>{translate("bodyMetrics.weight.label")}</Legend>
        <Input
          name="weight"
          defaultValue={initialValues.lbs}
          placeholder={translate("pounds")}
          label={translate("pounds")}
          isRequired
          maxLength={3}
          minLength={1}
          pattern="[0-9]*"
          mask="0[0][0]"
          inputMode="numeric"
          aria-label={translate("pounds")}
          icon={translate("units.pounds")}
          onChange={(e) => handlePoundsChange(e)}
          error={weightError}
        />
      </Fieldset>
    </div>
  );
};

export default BodyMetricsInput;
